import React, { useMemo } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import EmptyState from '../EmptyState';
import { READING_WORDING as RW, reading } from '../../domain/reading';
import { useLedger } from '../../data/useLedger';
import { chargeCategoryRows } from '../../domain/familyAccount';
import { useMoneyFormat } from '../../context/LocaleContext';
import s from './ChargeCategoriesDrawer.module.css';

// Charge Categories.
//
// MMS lets a studio name its own charge categories; what a teacher wants from
// that screen is where the money comes from. So rather than a second taxonomy
// on top of the ledger's five entry kinds, this summarises what is there — how
// much has been charged, discounted, paid and refunded, and how much of it came
// from lessons versus by hand.
export default function ChargeCategoriesDrawer({ open, onClose }) {
  const { entries, loading, failed } = useLedger();
  const formatMoney = useMoneyFormat();

  // Live entries only (a tombstone is two fields, issue #70), per kind; the
  // fold is `chargeCategoryRows`.
  const rows = useMemo(() => chargeCategoryRows(entries), [entries]);

  return (
    <Drawer open={open} onClose={onClose} title="Charge Categories" width={620}
      footer={<><FooterSpacer /><Button onClick={onClose}>Done</Button></>}>
      <div className={s.lead}>
        Every transaction on a family account falls into one of these kinds.
        &ldquo;From lessons&rdquo; counts the ones generated by taking attendance rather
        than entered by hand.
      </div>

      {rows.length === 0 ? (
        <EmptyState
          reading={reading(!loading, rows.length, failed)}
          title={RW['chargeCategories.emptyTitle']}
          description={RW['chargeCategories.emptyDescription']}
        />
      ) : (
        <div className={s.wrap}>
          <table className={s.table}>
            <thead>
              <tr>
                {['Kind', 'Count', 'From lessons', 'Total'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.kind}>
                  <td>{r.label}</td>
                  <td>{r.count}</td>
                  <td className={s.muted}>{r.generated || '\u2013'}</td>
                  <td>{formatMoney(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
